'use client';

/**
 * Chat settings modal – opened from the sidebar gear icon.
 *
 * Six preferences in one sheet: chat model, chat tabs, memory, the default
 * email account, the Meridian Google Calendar account, and the default
 * signature. The three checkbox/select preferences save on change; the email
 * trio shares one Save button because they save in one protocol.
 *
 * This file is the container. The pieces live in `./chat-settings/`:
 *  - `email-send-context.ts`       — pure: payload parsing, dirty tracking
 *  - `useEmailSendPreferences.ts`  — the email/calendar state + save protocol
 *  - `ChatModelSection.tsx`        — model picker + external-provider warning
 *  - `WorkspaceSections.tsx`       — chat tabs + memory toggles
 *  - `EmailSections.tsx`           — account, calendar, signature
 */

import React, { useCallback, useState } from 'react';
import { ResponsiveModal } from '@west-tennessee-consulting/ui/components/responsive-modal';
import { useGlobalModalStack } from '@west-tennessee-consulting/ui/components/global-modal-stack';
import { useModelPreference } from '../hooks/useModelPreference.js';
import { useMemoryPreference } from '../hooks/useMemoryPreference.js';
import { useChatTabs } from '../hooks/useChatTabs.js';
import { useEmailSendPreferences } from './chat-settings/useEmailSendPreferences.js';
import { ChatModelSection } from './chat-settings/ChatModelSection.js';
import { ChatTabsSection, MemorySection } from './chat-settings/WorkspaceSections.js';
import {
  DefaultEmailAccountSection,
  DefaultSignatureSection,
  MeridianCalendarSection,
} from './chat-settings/EmailSections.js';

const CHAT_SETTINGS_MODAL_ID = 'chat-settings';
/** How long the "Saved." confirmation stays up, in ms. */
const SAVED_FLASH_MS = 2000;

export interface ChatSettingsModalProps {
  apiBaseUrl?: string;
}

/**
 * Save-on-change with a transient "Saved." flash, shared by the model and
 * memory preferences. A failed save must NOT flash success.
 */
function useSavedFlash(): [boolean, (ok: boolean) => void] {
  const [saved, setSaved] = useState(false);
  const flash = useCallback((ok: boolean) => {
    if (!ok) return;
    setSaved(true);
    setTimeout(() => setSaved(false), SAVED_FLASH_MS);
  }, []);
  return [saved, flash];
}

export function ChatSettingsModal({ apiBaseUrl = '/api/meridian' }: ChatSettingsModalProps) {
  const { isModalOpen, closeModal } = useGlobalModalStack();
  const open = isModalOpen(CHAT_SETTINGS_MODAL_ID);

  const modelPref = useModelPreference({ apiBaseUrl, enabled: open });
  const [modelSaved, flashModelSaved] = useSavedFlash();
  const saveModelId = modelPref.saveModelId;
  const handleModelChange = useCallback(
    async (value: string) => {
      flashModelSaved(await saveModelId(value === '' ? null : value));
    },
    [saveModelId, flashModelSaved]
  );

  const chatTabs = useChatTabs();

  const memoryPref = useMemoryPreference({ apiBaseUrl, enabled: open });
  const [memorySaved, flashMemorySaved] = useSavedFlash();
  const saveAutoExtract = memoryPref.saveAutoExtract;
  const handleAutoExtractChange = useCallback(
    async (next: boolean) => {
      flashMemorySaved(await saveAutoExtract(next));
    },
    [saveAutoExtract, flashMemorySaved]
  );

  const emailPrefs = useEmailSendPreferences({ apiBaseUrl, open });

  return (
    <ResponsiveModal
      globalModalId={CHAT_SETTINGS_MODAL_ID}
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) closeModal(CHAT_SETTINGS_MODAL_ID);
      }}
      title="Chat settings"
    >
      <div className="space-y-4">
        <ChatModelSection
          modelPref={modelPref}
          saved={modelSaved}
          onChange={handleModelChange}
        />
        <ChatTabsSection enabled={chatTabs.enabled} onChange={chatTabs.setEnabled} />
        <MemorySection
          memoryPref={memoryPref}
          saved={memorySaved}
          onChange={handleAutoExtractChange}
        />
        <DefaultEmailAccountSection prefs={emailPrefs} />
        <MeridianCalendarSection prefs={emailPrefs} />
        <DefaultSignatureSection prefs={emailPrefs} />
      </div>
    </ResponsiveModal>
  );
}
